import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';

interface PrivateRouteProps {
  children: React.ReactElement;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean; // Only main-org admins or roles with canManageOrganisations
  requirePermission?: {
    canManageUsers?: boolean;
    canManageDepartments?: boolean;
    canApproveProjects?: boolean;
    canViewAllProjects?: boolean;
    canViewAllEvaluations?: boolean;
    canManageOrganisations?: boolean;
  };
  minPriority?: number; // Minimum role priority required (e.g., 50 for HOD+)
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requirePermission,
  minPriority,
}) => {
  const { isAuthenticated, user, loading, error } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for user data to load before making routing decisions
  if (!user && loading) {
    return (
      <div className="flex h-screen">
        <div className="w-14 bg-gray-900 shrink-0 hidden lg:block" />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  // If we still don't have user data after loading and there's an error, redirect to login
  if (!user && !loading && error) {
    return <Navigate to="/login" replace />;
  }

  // If no user data and not loading, but no error yet, keep showing loading
  if (!user && !loading && !error) {
    return (
      <div className="flex h-screen">
        <div className="w-14 bg-gray-900 shrink-0 hidden lg:block" />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  // At this point we should have user data
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.isFirstLogin) {
    return <Navigate to="/change-password" replace />;
  }

  // Check admin requirement (legacy support + new organization role check)
  // Full admin = priority >= 90 (CEO, COO, CTO, CFO, Super Admin)
  // HODs (priority 50) should use routes with minPriority or requirePermission instead
  const isFullAdmin = user.role === 'admin' || (user.organizationRole?.priority ?? 0) >= 90;

  console.log("[ISFULLADMIN] debugger", isFullAdmin, "requireAdmin:", requireAdmin);

  // If route requires admin and user is not a full admin, deny access
  if (requireAdmin && !isFullAdmin) {
    return <Navigate to="/employee/dashboard" replace />;
  }

  // Super-admin guard: only main-org admins or roles with canManageOrganisations
  if (requireSuperAdmin && !user.canManageOrganisations) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Check minimum priority requirement
  // Full admins (role='admin' or priority>=90) always pass priority gates
  if (minPriority !== undefined && !isFullAdmin) {
    const userPriority = user.organizationRole?.priority ?? 0;
    if (userPriority < minPriority) {
      return <Navigate to="/employee/dashboard" replace />;
    }
  }

  // Check specific permission requirements
  if (requirePermission) {
    const orgRole = user.organizationRole;

    // Admin always has access
    if (user.role !== 'admin') {
      let hasRequiredPermission = false;

      if (requirePermission.canManageUsers && orgRole?.canManageUsers) {
        hasRequiredPermission = true;
      }
      if (requirePermission.canManageDepartments && orgRole?.canManageDepartments) {
        hasRequiredPermission = true;
      }
      if (requirePermission.canApproveProjects && orgRole?.canApproveProjects) {
        hasRequiredPermission = true;
      }
      if (requirePermission.canViewAllProjects && orgRole?.canViewAllProjects) {
        hasRequiredPermission = true;
      }
      if (requirePermission.canViewAllEvaluations && orgRole?.canViewAllEvaluations) {
        hasRequiredPermission = true;
      }
      if (requirePermission.canManageOrganisations && user.canManageOrganisations) {
        hasRequiredPermission = true;
      }

      // If any permission was required but none matched
      const anyPermissionRequired = Object.values(requirePermission).some(Boolean);
      if (anyPermissionRequired && !hasRequiredPermission) {
        return <Navigate to="/employee/dashboard" replace />;
      }
    }
  }

  return children;
};

export default PrivateRoute;
